import React, { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { getStudentTickets } from "../../../services/dataService";
import { mergeTickets } from "../../../utils/studentDetail";
import { ticketsShowingLabel } from "../../../utils/kitPresentation";
import { hasPermission, permissions } from "../../../config/permissions";
import { TableCard } from "../../../components/kit/SectionCard";
import Pager from "../../../components/kit/Pager";
import { TableMessageRow } from "../../../components/kit/TableParts";
import TicketRow from "../../Inbox/TicketRow";
import { errorText } from "./TabState";

const COLUMNS = ["Ticket", "Reason", "Status", "Last activity", "Actions"];

// The tickets this person raised and the system tickets about their lessons, one
// list, newest activity first, capped at 50 (D35), drawn from Figma (047; 10810:61045).
// The rows are the Inbox's (TicketRow), and the eye opens the ticket page (047 D54). The KPI
// cards are backlog (D30). `read` is getStudentTickets, or getTutorTickets on the tutor page
// (047 step 4).
export const TicketsCard = ({ uid, read, timeZone, userRole, emptyText }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const canOpenInbox = hasPermission(userRole, permissions.VIEW_INBOX);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { raised, about } = await read(uid);
      setRows(mergeTickets(raised, about));
    } catch (e) {
      console.error("Error loading tickets:", e);
      setError(errorText(e, "Failed to load the tickets."));
    } finally {
      setLoading(false);
    }
  }, [uid, read]);

  useEffect(() => {
    load();
  }, [load]);

  const settled = !loading && !error;
  const count = `${rows.length}${rows.length >= 50 ? "+" : ""} ${rows.length === 1 ? "ticket" : "tickets"}`;

  return (
    <TableCard title="Tickets" subtitle={settled ? count : ""} footer={<Pager label={settled ? ticketsShowingLabel(rows.length) : ""} />}>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {COLUMNS.map((c) => (
                <TableCell key={c}>{c}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading || error || rows.length === 0 ? (
              <TableMessageRow colSpan={COLUMNS.length} loading={loading} error={error} onRetry={load} emptyText={emptyText} />
            ) : (
              rows.map((t) => <TicketRow key={t.id} ticket={t} timeZone={timeZone} canOpen={canOpenInbox} />)
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </TableCard>
  );
};

const TicketsTab = () => {
  const { student, userRole } = useOutletContext();
  return (
    <TicketsCard uid={student.id} read={getStudentTickets} timeZone={student.timeZone} userRole={userRole} emptyText="No tickets for this student." />
  );
};

export default TicketsTab;
